import { Box, Typography, IconButton } from '@mui/material';
import { PersonalityRate } from '../../../types/damage';
import { MOVE_DAMAGE_CLASS_PHYSICAL, MOVE_DAMAGE_CLASS_SPECIAL, isBodyPressMove, isIkasamaMove, isShellSideArmMove, isTerabrastMove } from '../../../move/move';
import { AttackPokemonData } from '../../../types/attack';
import { AttackStatInputs } from '../../../lib/stat';
import { MAX_STAT_EFFORT, MAX_TOTAL_EFFORT } from '../../../constants/effort';
import EffortSelectField from '../../../components/EffortSelectField';

interface OffencePokemonEffortInputProps {
  attackPokemon: AttackPokemonData
  handleUpdateAttackPokemonStats: (id: string, newStatInputs: AttackStatInputs) => void
}

function OffencePokemonEffortInput({ attackPokemon, handleUpdateAttackPokemonStats }: OffencePokemonEffortInputProps) {
  if (!attackPokemon.selectedMoveId || !attackPokemon.selectedMove) {
    return null;
  }

  // 現在の合計EV計算
  const calculateTotalEvs = (statInputs: AttackStatInputs) => {
    return statInputs.attack_effort +
      statInputs.special_attack_effort +
      statInputs.defense_effort +
      statInputs.special_defense_effort +
      statInputs.hp_effort +
      statInputs.speed_effort;
  };

  // const currentTotalEvs = calculateTotalEvs(attackPokemon.statInputs);

  // EV制限付きステータス更新関数
  const updateStatsWithLimit = (statName: keyof AttackStatInputs, value: number) => {
    const sanitized = Math.max(0, Math.min(MAX_STAT_EFFORT, Math.floor(value)));
    const currentValue = attackPokemon.statInputs[statName] || 0;
    const otherTotal = calculateTotalEvs(attackPokemon.statInputs) - currentValue;
    const maxAllowed = Math.max(0, MAX_TOTAL_EFFORT - otherTotal);
    const clampedValue = Math.min(sanitized, maxAllowed);
    const newStatInputs = { ...attackPokemon.statInputs, [statName]: clampedValue };
    handleUpdateAttackPokemonStats(attackPokemon.id, newStatInputs);
  };

  const handlePersonalityToggle = (field: keyof AttackStatInputs, targetValue: PersonalityRate) => {
    const current = (attackPokemon.statInputs[field] as PersonalityRate | undefined) ?? 1.0;
    const nextValue = current === targetValue ? 1.0 : targetValue;
    const newStatInputs = { ...attackPokemon.statInputs, [field]: nextValue };
    handleUpdateAttackPokemonStats(attackPokemon.id, newStatInputs);
  };

  const renderEffortControl = (
    label: string,
    effortField: keyof AttackStatInputs,
    personalityField?: keyof AttackStatInputs,
  ) => {
    const effortValue = attackPokemon.statInputs[effortField] || 0;
    const personalityValue = personalityField ? ((attackPokemon.statInputs[personalityField] as PersonalityRate) ?? 1.0) : undefined;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', minWidth: '1.5rem', textAlign: 'center' }}>
          {label}
        </Typography>
        <EffortSelectField value={effortValue} onChange={(value) => updateStatsWithLimit(effortField, value)} />
        {personalityField && (
          <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
            <IconButton
              size="small"
              onClick={() => handlePersonalityToggle(personalityField, 0.9)}
              sx={{
                width: 18,
                height: 18,
                fontSize: '0.6rem',
                border: '1px solid #555',
                borderRadius: 0.125,
                minWidth: 0,
                padding: 0,
                backgroundColor: personalityValue === 0.9 ? '#1976d2' : 'white',
                color: personalityValue === 0.9 ? 'white' : '#555',
                '&:hover': {
                  backgroundColor: personalityValue === 0.9 ? '#1565c0' : '#f5f5f5',
                },
              }}
            >
              -
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handlePersonalityToggle(personalityField, 1.1)}
              sx={{
                width: 18,
                height: 18,
                fontSize: '0.6rem',
                border: '1px solid #555',
                borderRadius: 0.125,
                minWidth: 0,
                padding: 0,
                backgroundColor: personalityValue === 1.1 ? '#1976d2' : 'white',
                color: personalityValue === 1.1 ? 'white' : '#555',
                '&:hover': {
                  backgroundColor: personalityValue === 1.1 ? '#1565c0' : '#f5f5f5',
                },
              }}
            >
              +
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
        努力値
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {isBodyPressMove(attackPokemon.selectedMove) ? (
          // Body Press uses Defense stat for damage calculation
          renderEffortControl('B', 'defense_effort', 'defense_personality')
        ) : isIkasamaMove(attackPokemon.selectedMove) ? (
          // イカサマ: 防御側の攻撃力を参照するため、攻撃の努力値入力欄を表示（ただし防御側の値を反映）
          renderEffortControl('A', 'attack_effort', 'attack_personality')
        ) : isShellSideArmMove(attackPokemon.selectedMove) ? (
          // シェルアームズ: 攻撃と特攻の両方を表示
          <>
            {renderEffortControl('A', 'attack_effort', 'attack_personality')}
            {renderEffortControl('C', 'special_attack_effort', 'special_attack_personality')}
          </>
        ) : attackPokemon.selectedMove?.damage_class_number === MOVE_DAMAGE_CLASS_PHYSICAL ||
          (isTerabrastMove(attackPokemon.selectedMove) && attackPokemon.pokemon.attack > attackPokemon.pokemon.special_attack) ? (
          renderEffortControl('A', 'attack_effort', 'attack_personality')
        ) : attackPokemon.selectedMove?.damage_class_number === MOVE_DAMAGE_CLASS_SPECIAL ||
          (isTerabrastMove(attackPokemon.selectedMove) && attackPokemon.pokemon.attack <= attackPokemon.pokemon.special_attack) ? (
          renderEffortControl('C', 'special_attack_effort', 'special_attack_personality')
        ) : null}
      </Box>
    </Box>
  );
}

export default OffencePokemonEffortInput
